// --- START OF FILE firestoreService.js ---

import { 
    collection, 
    addDoc, 
    serverTimestamp, 
    getDocs, 
    query, 
    orderBy,
    doc,
    updateDoc,
    deleteDoc,
    where,
    limit,
    onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Sets up a real-time listener for pending match invitations for a given user.
 * @param {string} userId - The UID of the user.
 * @param {function} callback - A function to be called with the array of invites whenever they change.
 * @returns {function} An unsubscribe function to detach the listener.
 */
export const listenForInvites = (userId, callback) => {
    if (!userId) return () => {}; // Return a no-op unsubscribe function
    const matchesCollectionRef = collection(db, 'matches');
    const q = query(
        matchesCollectionRef,
        where('matchData.playerIds', 'array-contains', userId),
        where('status', '==', 'pending'),
        where('matchData.createdBy', '!=', userId)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const invites = [];
        querySnapshot.forEach((doc) => {
            invites.push({ id: doc.id, ...doc.data() });
        });
        callback(invites);
    });

    return unsubscribe; // Return the function to stop listening
};

/**
 * Checks Firestore for an active match for a given user.
 * @param {string} userId - The UID of the user.
 * @returns {Promise<object|null>} A promise that resolves to the match object or null.
 */
export const getActiveMatchForUser = async (userId) => {
    if (!userId) return null;
    const matchesCollectionRef = collection(db, 'matches');
    const q = query(
        matchesCollectionRef,
        where('matchData.playerIds', 'array-contains', userId),
        where('status', '==', 'active'),
        limit(1) // A user should only have one active match at a time.
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
        const matchDoc = querySnapshot.docs[0];
        return { id: matchDoc.id, ...matchDoc.data() };
    }

    return null;
};

/**
 * Creates a new match document in the top-level 'matches' collection to initiate a challenge.
 * @param {object} matchData - The match setup data from SetupView.
 * @param {object} initialGameState - The default game state object.
 * @returns {Promise<DocumentReference>} A promise that resolves to the new match document reference.
 */
export const createMatch = async (matchData, initialGameState) => {
    const matchesCollectionRef = collection(db, 'matches');
    
    const newMatch = {
        matchData: matchData,
        gameState: initialGameState,
        shotLog: [],
        status: 'pending', // 'pending', 'active', 'completed', 'declined', 'cancelled'
        createdAt: serverTimestamp(),
    };

    return addDoc(matchesCollectionRef, newMatch);
};

/**
 * Updates a live match document in the top-level 'matches' collection.
 * @param {string} matchId - The ID of the match document to update.
 * @param {object} dataToUpdate - An object with the fields to update.
 */
export const updateMatch = async (matchId, dataToUpdate) => {
    if (!matchId) return;
    const matchDocRef = doc(db, 'matches', matchId);
    return updateDoc(matchDocRef, {
        ...dataToUpdate,
        lastUpdatedAt: serverTimestamp()
    });
};

/**
 * Fetches pending match invitations for a given user.
 * This is kept for the HomeView which does a one-time fetch, but the listener is primary.
 * @param {string} userId - The UID of the user.
 * @returns {Promise<Array>} A promise that resolves to an array of pending match objects.
 */
export const getMatchInvites = async (userId) => {
    if (!userId) return [];
    const matchesCollectionRef = collection(db, 'matches');
    const q = query(
        matchesCollectionRef,
        where('matchData.playerIds', 'array-contains', userId),
        where('status', '==', 'pending'),
        where('matchData.createdBy', '!=', userId)
    );

    const querySnapshot = await getDocs(q);
    const invites = [];
    querySnapshot.forEach((doc) => {
        invites.push({ id: doc.id, ...doc.data() });
    });
    return invites;
};

/**
 * Saves a completed match to the history sub-collection of the CURRENT user.
 * @param {string} userId - The UID of the current user saving the match.
 * @param {object} match - The completed match object.
 */
export const saveMatch = async (userId, match) => {
    if (!userId || !match) {
        console.error("Cannot save match, missing userId or match data");
        return;
    }
    const matchRecord = { ...match, completedAt: serverTimestamp() };
    // This correctly creates a new document with an auto-generated ID inside the user's 'matches' sub-collection.
    return await addDoc(collection(db, 'users', userId, 'matches'), matchRecord);
};

/**
 * Fetches the match history for a given user from their private sub-collection.
 * @param {string} userId - The UID of the user.
 * @param {object} [options] - Options for filtering history.
 * @param {boolean} [options.includeGhost=true] - Whether to include ghost/practice matches.
 * @returns {Promise<Array>} A promise that resolves to an array of match objects.
 */
export const getMatchHistory = async (userId, options = { includeGhost: true }) => {
    if (!userId) return [];
    const matchesCollectionRef = collection(db, 'users', userId, 'matches');
    const q = query(matchesCollectionRef, orderBy('completedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const matches = [];
    const { includeGhost } = options;
    querySnapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        if (!includeGhost && data.matchData?.player2Id === 'ghost') {
            return; // Skip practice matches
        }
        matches.push(data);
    });
    
    return matches;
};

/**
 * Fetches only real-player matches for a given user.
 */
export const getRealMatchHistory = async (userId) => {
    return getMatchHistory(userId, { includeGhost: false });
};

/**
 * Fetches a list of all user profiles.
 * @returns {Promise<Array>} A promise that resolves to an array of user profile objects.
 */
export const getAllUserProfiles = async () => {
    const usersCollectionRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersCollectionRef);

    const profiles = [];
    querySnapshot.forEach((doc) => {
        if (doc.data().name) { 
            profiles.push({
                uid: doc.id,
                ...doc.data()
            });
        }
    });

    return profiles;
};

/**
 * Updates a user's profile information in Firestore.
 * @param {string} userId - The UID of the user to update.
 * @param {object} profileData - An object containing the fields to update.
 */
export const updateUserProfile = async (userId, profileData) => {
    if (!userId) return;
    const userDocRef = doc(db, 'users', userId);
    return updateDoc(userDocRef, profileData);
};

/**
 * Deletes a user's document from the 'users' collection.
 * @param {string} userId - The UID of the user whose document to delete.
 */
export const deleteUserDocument = async (userId) => {
    if (!userId) return;
    const userDocRef = doc(db, 'users', userId);
    return deleteDoc(userDocRef);
};